import { cssJoin } from '@volvo-cars/css/utils';
import { EmailInput, TextArea } from '@volvo-cars/react-forms';
import { CSSProperties, useRef } from 'react';
import AnimateHeight from 'react-animate-height';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { Transition } from 'react-transition-group';

import { simpleEmailRegex } from '@vcc-www/utils/validation';

import styles from './Feedback.module.css';
import ThumbButton from './ThumbButton';

export type FeedbackRating = -1 | 0 | 1;

export type Inputs = {
  email: string;
  message: string;
};

export type FeedbackDisplayProps = {
  isLoading: boolean;
  success: boolean;
  feedbackRating: number;
  errors: FieldErrors<Inputs>;
  onClickThumb: (feedbackRating: FeedbackRating) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  register: UseFormRegister<Inputs>;
};

export const emailLabel = 'Email (optional)';
export const emailErrorLabel = 'Enter a valid email address';
export const feedbackLabel = 'Do you have any input? (Optional)';
export const feedbackSentText = 'Thank you for your feedback!';

const transitionMS = 500;

export const FeedbackDisplay: React.FC<FeedbackDisplayProps> = ({
  isLoading,
  success,
  feedbackRating,
  errors,
  onClickThumb,
  onSubmit,
  register,
}) => {
  const nodeRef = useRef(null);
  const expanded = feedbackRating !== 0;

  return (
    <div className="flex-col items-center" style={{ position: 'relative' }}>
      <p className="text-secondary">Was this helpful?</p>
      <div className="flex mt-16 gap-32">
        <ThumbButton
          onClick={() => onClickThumb(1)}
          disabled={isLoading}
          selected={feedbackRating === 1}
        />
        <ThumbButton
          onClick={() => onClickThumb(-1)}
          disabled={isLoading}
          selected={feedbackRating === -1}
          direction="down"
        />
      </div>
      <Transition nodeRef={nodeRef} timeout={transitionMS} in={expanded}>
        {(transitionState) => {
          const show = transitionState.startsWith('enter');
          const formStyle = {
            '--opacity': show ? 1 : 0,
            '--top': show ? 0 : '10px',
          } as CSSProperties;

          return (
            <AnimateHeight
              className="w-full"
              duration={transitionMS}
              height={expanded ? 'auto' : 0}
            >
              <form
                onSubmit={onSubmit}
                className={cssJoin(
                  styles.form,
                  'flex-col items-center m-auto mt-32 w-full stack-16',
                )}
                style={formStyle}
              >
                <EmailInput
                  className="w-full"
                  disabled={isLoading}
                  label={emailLabel}
                  maxLength={320}
                  errorMessage={
                    errors?.email ? emailErrorLabel : (undefined as any)
                  }
                  {...register('email', {
                    pattern: simpleEmailRegex,
                  })}
                />
                <TextArea
                  className="w-full"
                  disabled={isLoading}
                  label={feedbackLabel}
                  maxLength={512}
                  {...register('message')}
                />
                <button
                  type="submit"
                  aria-label="send feedback"
                  aria-disabled={isLoading}
                  className={cssJoin(
                    styles.submitButton,
                    'mt-32 button-outlined',
                  )}
                >
                  Send
                </button>
              </form>
            </AnimateHeight>
          );
        }}
      </Transition>
      <Transition
        nodeRef={nodeRef}
        timeout={transitionMS}
        in={success}
        mountOnEnter
        unmountOnExit
      >
        {(transitionState) => {
          const show = transitionState === 'entered';

          return (
            <p
              className={cssJoin(styles.success, 'font-medium')}
              ref={nodeRef}
              style={{ '--opacity': show ? 1 : 0 } as CSSProperties}
            >
              {feedbackSentText}
            </p>
          );
        }}
      </Transition>
    </div>
  );
};

export default FeedbackDisplay;
